import React from 'react';
import { X } from 'lucide-react';
import { Badge } from '@/components/ui/Badge/Badge';
import { HStack } from '@/components/ui/Stack';
import { SimpleTooltip } from '@/components/ui/Tooltip';
import type { SearchCriteria } from '@/services/ResearchService';

interface FilterSummaryBadgeProps {
  criteria: SearchCriteria;
  onRemoveFilter?: (filterKey: string) => void;
  maxVisible?: number;
  compact?: boolean;
}

interface FilterChip {
  label: string;
  key: string;
  value: any;
}

export function FilterSummaryBadge({
  criteria,
  onRemoveFilter,
  maxVisible = 3,
  compact = false,
}: FilterSummaryBadgeProps) {
  // Convert criteria to filter chips
  const getFilterChips = (): FilterChip[] => {
    const chips: FilterChip[] = [];

    if (criteria.min_rooms) {
      chips.push({ 
        label: `${criteria.min_rooms}+ rooms`, 
        key: 'min_rooms',
        value: criteria.min_rooms 
      });
    }
    if (criteria.min_conference_capacity) {
      chips.push({ 
        label: `Conference ${criteria.min_conference_capacity}+`, 
        key: 'min_conference_capacity',
        value: criteria.min_conference_capacity
      });
    }
    if (criteria.max_airport_distance_km) {
      chips.push({ 
        label: `<${criteria.max_airport_distance_km}km airport`, 
        key: 'max_airport_distance_km',
        value: criteria.max_airport_distance_km
      });
    }
    if (criteria.min_star_rating) {
      chips.push({ 
        label: `${criteria.min_star_rating}+ stars`, 
        key: 'min_star_rating',
        value: criteria.min_star_rating
      });
    }
    if (criteria.has_spa) {
      chips.push({ label: 'Spa', key: 'has_spa', value: true });
    }
    if (criteria.min_restaurants) {
      chips.push({ 
        label: `${criteria.min_restaurants}+ Restaurant${criteria.min_restaurants > 1 ? 's' : ''}`, 
        key: 'min_restaurants',
        value: criteria.min_restaurants
      });
    }
    if (criteria.min_bars) {
      chips.push({ 
        label: `${criteria.min_bars}+ Bar${criteria.min_bars > 1 ? 's' : ''}`, 
        key: 'min_bars',
        value: criteria.min_bars
      });
    }
    if (criteria.min_pools) {
      chips.push({ 
        label: `${criteria.min_pools}+ Pool${criteria.min_pools > 1 ? 's' : ''}`, 
        key: 'min_pools',
        value: criteria.min_pools
      });
    }
    if (criteria.beach_access === 'beachfront') {
      chips.push({ label: 'Beachfront', key: 'beach_access', value: 'beachfront' });
    }
    if (criteria.beach_access === 'beach_access') {
      chips.push({ label: 'Beach Access', key: 'beach_access', value: 'beach_access' });
    }
    if (criteria.all_inclusive) {
      chips.push({ label: 'All-Inclusive', key: 'all_inclusive', value: true });
    }
    if (criteria.property_type === 'adults_only') {
      chips.push({ label: 'Adults Only', key: 'property_type', value: 'adults_only' });
    }
    if (criteria.property_type === 'family_friendly') {
      chips.push({ label: 'Family Friendly', key: 'property_type', value: 'family_friendly' });
    }
    if (criteria.property_type === 'mixed') {
      chips.push({ label: 'Mixed', key: 'property_type', value: 'mixed' });
    }
    if (criteria.has_golf) {
      chips.push({ label: 'Golf', key: 'has_golf', value: true });
    }
    if (criteria.min_tripadvisor_rating) {
      chips.push({ 
        label: `TripAdvisor ${criteria.min_tripadvisor_rating}+`, 
        key: 'min_tripadvisor_rating',
        value: criteria.min_tripadvisor_rating
      });
    }

    return chips;
  };

  const allChips = getFilterChips();
  const visibleChips = compact ? allChips.slice(0, maxVisible) : allChips;
  const hiddenCount = allChips.length - visibleChips.length;

  if (allChips.length === 0) return null;

  // Build tooltip content for hidden filters
  const hiddenFilters = allChips.slice(maxVisible).map(chip => chip.label).join(', ');

  return (
    <HStack gap={1} align="center" className="flex-wrap">
      {visibleChips.map((chip) => (
        <Badge
          key={chip.key}
          variant="secondary"
          className="text-xs flex items-center gap-1 pr-1 py-0.5"
        >
          <span className="max-w-[120px] truncate">{chip.label}</span>
          {onRemoveFilter && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemoveFilter(chip.key);
              }}
              className="hover:bg-gray-300 rounded-full p-0.5 transition-colors ml-0.5"
              aria-label={`Remove ${chip.label} filter`}
            >
              <X className="w-2.5 h-2.5" />
            </button>
          )}
        </Badge>
      ))}
      
      {hiddenCount > 0 && compact && (
        <SimpleTooltip content={`Also filtering by: ${hiddenFilters}`}>
          <Badge variant="secondary" className="text-xs py-0.5">
            +{hiddenCount} more
          </Badge>
        </SimpleTooltip>
      )}
    </HStack>
  );
}
